// This component renders a modal that allows users to upload an image for a property.  
// Users can either capture a photo using their device's camera or upload an image file from their device. 
// The component also provides functionality to switch between front and rear cameras, and to delete captured or uploaded 
// images with a confirmation popup. Once the user submits the image, it is uploaded to the server and associated with the property. 
// The component also displays success or error toasts based on the outcome of the upload and save operations.
import { useState, useRef } from "react";
import {
  UPLOAD_IMAGE_API,
  UPDATE_PROPERTY_IMAGE,
  DELETE_IMAGE_API,
} from "../../api/apiPath";
import { hostName } from "../../config/config";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import http from "../../api/server";
import { useParams, generatePath } from "react-router-dom";
import Toaster from "./Toaster";
import Webcam from "react-webcam";

function UploadPropertyImage({
  propertyId,
  buildingid,
  buildingName,
  handleCancel,
  propertyName,
  inspectionRefnumber,
  handleSuccess,
}) {
  const { clientNameParam } = useParams();
  const [capturedImages, setCapturedImages] = useState([]);
  const [savedImages, setSavedImages] = useState([]);
  const { userObj } = useSelector((state) => state.user);
  const [error, setError] = useState(null);
  // const navigate = useNavigate();
  const [toastData, setToastData] = useState({
    toastShow: false,
    toastType: "",
    toastString: "",
  });
  const [showCamera, setShowCamera] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const webcamRef = useRef(null);
  const fileRef = useRef(null);
  const [facingMode, setFacingMode] = useState("user");
  const ImageProxyBaseUrl = hostName + "/api/Inspection/api/image?blobUrl=";
  const [floorPlanBlobUrl, setFloorPlanBlobUrl] = useState("");
  const videoConstraints = {
    width: 640,
    height: 480,
    facingMode: facingMode,
  };
  const switchCamera = () => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  };
  const closeCamera = () => {
    setShowCamera(false);
  };
  const capturePhoto = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    if (imageSrc) {
      const newImages = [...capturedImages, imageSrc];
      updateImages(newImages, "");
      setShowCamera(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.size > 10 * 1024 * 1024) {
      setToastData({
        toastShow: true,
        toastType: "failure",
        toastString: "File size should not exceed 10 MB",
      });
      fileRef.current.value = "";
      return;
    }
    if (file) {
      const nameOfFile = file.name;
      const reader = new FileReader();
      reader.onloadend = () => {
        const newImages = [...capturedImages, reader.result];
        updateImages(newImages, nameOfFile);
      };
      reader.readAsDataURL(file);
    }
  };
  const updateImages = async (newImages, nameOfFile) => {
    setCapturedImages(newImages);
    let newArr = [];
    if (newImages && newImages.length > 0) {
      const uploadResult = await uploadImageAPI(newImages, nameOfFile);
      uploadResult !== ""
        ? (newArr = [...newArr, uploadResult])
        : (newArr = []);
    } else {
      newArr = [];
    }
    console.log("", newArr);
    setSavedImages(newArr);
  };

  const base64ToFile = (base64String, filename) => {
    const arr = base64String.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);

    const extensionMap = {
      jpeg: "jpeg",
      jpg: "jpg",
      png: "png",
      gif: "gif",
      webp: "webp",
      pdf: "pdf",
    };

    const mimeExt = mime.split("/")[1];
    const ext = extensionMap[mimeExt] || mimeExt;

    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    const cleanName = filename.replace(/\.[^/.]+$/, "");
    // const isIncludeExtension = filename?.includes(`.${ext}`) ? true : false;
    // return isIncludeExtension ? new File([u8arr], `${filename}`, { type: mime }) : new File([u8arr], `${filename}.${ext}`, { type: mime });
    return new File([u8arr], `${cleanName}.${ext}`, { type: mime });
  };

  const uploadImageAPI = async (newImages, nameofFile) => {
    const latestImage = newImages[newImages.length - 1];
    const name = nameofFile ? nameofFile : `Building_Image_${Date.now()}`;
    const file = base64ToFile(latestImage, name);
    const formDataToUpload = new FormData();
    formDataToUpload.append("File", file);
    formDataToUpload.append("Client", clientNameParam);
    formDataToUpload.append("Property", propertyName);
    formDataToUpload.append("InspectionDate", new Date().toISOString());
    try {
      const response = await fetch(hostName + UPLOAD_IMAGE_API, {
        method: "POST",
        body: formDataToUpload,
        headers: {
          Authorization: "Bearer " + userObj?.token,
        },
      });
      if (!response.status) {
        throw new Error(`HTTP error! status: ${response?.status}`);
      }
      const dataResponse = await response?.json();
      if (response.status === 200) {
        setFloorPlanBlobUrl(dataResponse?.result.blobUrl);
        const isFloorpdf = dataResponse?.result?.blobName.includes(".pdf")
          ? true
          : false;
        let pdfurl;
        if (isFloorpdf) {
          const blob = base64ToBlob(
            dataResponse?.result?.fileData,
            "application/pdf",
          );
          pdfurl =
            URL.createObjectURL(blob) +
            "?filename=" +
            (dataResponse?.result?.blobName || "floorplan.pdf");
          // setFloorPlanPdfData(dataResponse?.result?.fileData);
        }
        return isFloorpdf ? pdfurl : dataResponse?.result?.blobUrl;
      } else {
        const msg =
          dataResponse?.error || "Error in uploading image, please try again";
        setToastData({
          toastShow: true,
          toastType: "failure",
          toastString: msg,
        });
        fileRef.current.value = "";
        setCapturedImages([]);
        return "";
      }
    } catch (error) {
      setError(error?.data);
      console.error("Error uploading image:", error, error?.data);
      const msg =
        error?.data?.error || "Error in uploading image, please try again";
      setToastData({
        toastShow: true,
        toastType: "failure",
        toastString: msg,
      });
      fileRef.current.value = "";
      setCapturedImages([]);
      return "";
    }
  };

  const base64ToBlob = (base64, contentType = "application/pdf") => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);

    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  };
  // const gotoDashBoard = (propertyId) => {
  //     const pathTemplate = "/client/:clientNameParam/dashboard/:propertyMasterId/:inspectionRefnumber";
  //         const params = {
  //           clientNameParam: clientNameParam,
  //           propertyMasterId: propertyId,
  //           inspectionRefnumber: inspectionRefnumber
  //         };

  //         const fullPath = generatePath(pathTemplate, params);
  //         navigate(fullPath);
  // };

  // const gotoScanQRCode = (propertyId) => {
  //   const pathTemplate =
  //     "/client/:clientNameParam/scanNewDoor/:propertyMasterId/:inspectionRefnumber";

  //   const params = {
  //     clientNameParam,
  //     propertyMasterId: propertyId,
  //     inspectionRefnumber,
  //   };

  //   const fullPath = generatePath(pathTemplate, params);
  //   navigate(fullPath);
  // };

  const savePropertyImage = async () => {
    if (savedImages.length > 0) {
      setIsLoading(true);
      const saveData = {
        propertyMasterId: propertyId,
        propertyImage: savedImages.length > 0 ? floorPlanBlobUrl : "",
        buildingId: buildingid,
      };
      const jsonData = JSON.stringify(saveData);
      try {
        const response = await http.put(UPDATE_PROPERTY_IMAGE, jsonData);
        if (!response.status) {
          throw new Error(`HTTP error! status: ${response?.status}`);
        }
        const mess = buildingName
          ? `The uploaded image has been saved for ${buildingName}, ${propertyName}`
          : `The uploaded image has been saved for ${propertyName}`;
        if (response.status === 200) {
          setToastData({
            toastShow: true,
            toastType: "success",
            toastString: mess,
          });

          setTimeout(
            () => handleSuccess(propertyId, inspectionRefnumber, buildingid),
            1500,
          );
        } else {
          setToastData({
            toastShow: true,
            toastType: "failure",
            toastString: "Error in saving image, please try again",
          });
          setIsLoading(false);
          handleCancel();
        }
      } catch (error) {
        setError(error?.data);
      }
    }
  };
  const closeButton = () => {
    setToastData({
      toastShow: false,
      toastData: "",
      toastType: "",
    });
  };

  const removeImage = (indexToRemove) => {
    const newImages = capturedImages.filter(
      (_, index) => index !== indexToRemove,
    );
    deleteImages(newImages, indexToRemove);
    fileRef.current.value = "";
  };
  const deleteImages = (newImages, indexToRemove) => {
    setCapturedImages(newImages);
    let imageToDelete = floorPlanBlobUrl;
    const combined = savedImages.filter((_, index) => index !== indexToRemove);
    setSavedImages(combined);
    deleteImageAPI(imageToDelete);
  };
  const deleteImageAPI = async (imageToDelete) => {
    try {
      const response = await http.delete(DELETE_IMAGE_API + imageToDelete);
      if (!response.status) {
        throw new Error(`HTTP error! status: ${response?.status}`);
      }
    } catch (error) {
      setError(error?.data);
    }
  };

  if (error)
    return (
      <>
        <div className="inCenterDiv">
          <i className="bi bi-exclamation-triangle red"></i>
          <h3>Something went wrong!</h3>
          <p>{error?.message}</p>
          <div className="col-sm-12 mb-4 text-center">
            <Link
              to={generatePath("/client/:clientNameParam/", {
                clientNameParam,
              })}
            >
              Go back to Login
            </Link>
          </div>
        </div>
      </>
    );
  return (
    <div className="modal-overlay">
      <Toaster
        show={toastData.toastShow}
        toastType={toastData.toastType}
        title={toastData.toastString}
        theme={"dark"}
        position="top-center"
        closeButton={() => {
          closeButton();
        }}
      />
      <div className="modal-content" style={{ height: "auto", width: "80%" }}>
        <span
          className="close"
          style={{ top: "0px" }}
          onClick={() => handleCancel()}
        >
          &times;
        </span>
        <h6 className="modal-header">
          Upload Building Image for {propertyName} - {buildingName}
        </h6>{" "}
        {/* Modal Header */}
        <div className="modal-body" style={{ backgroundColor: "white" }}>
          <h5 style={{ textAlign: "justify" }}>Building Photo</h5>
          <div className="d-flex gap-3 pb-4 align-items-center pt-2">
            <input
              type="file"
              accept=".jpg, .jpeg, .png, .gif, .pdf"
              onChange={handleFileChange}
              className="form-control"
              id="chooseFiles"
              disabled={capturedImages.length >= 1}
              ref={fileRef}
            />
            {!showCamera && (
              <div>
                <i
                  className="fa-solid fa-camera fa-xl mainCamera"
                  onClick={() => {
                    if (!(capturedImages.length >= 1)) {
                      setShowCamera(true);
                    }
                  }}
                  title="Open Camera"
                  style={{
                    cursor:
                      capturedImages.length >= 1 ? "not-allowed" : "pointer",
                    opacity: capturedImages.length >= 1 ? 0.5 : 1,
                  }}
                ></i>
              </div>
            )}
          </div>
          {showCamera && (
            <div>
              <Webcam
                audio={false}
                ref={webcamRef}
                height={280}
                screenshotFormat="image/jpeg"
                width={290}
                videoConstraints={videoConstraints}
              />
              <div
                className="d-flex flex-wrap gap-3"
                style={{ justifyContent: "center" }}
              >
                <i
                  className="fa-solid fa-camera fa-xl"
                  onClick={capturePhoto}
                  disabled={capturedImages.length >= 1}
                  style={{ cursor: "pointer" }}
                  title="Capture Photo"
                ></i>
                <i
                  className="fa-solid fa-camera-rotate fa-xl"
                  onClick={switchCamera}
                  style={{ cursor: "pointer" }}
                  title="Switch Camera"
                ></i>
                <i
                  className="fa-solid fa-xmark fa-xl"
                  onClick={closeCamera}
                  style={{ cursor: "pointer" }}
                  title="Close Camera"
                ></i>
              </div>
            </div>
          )}
          {capturedImages.length > 0 && (
            <div style={{ marginTop: "20px" }}>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                {savedImages.map((img, index) => {
                  if (img !== null && img !== undefined && img !== "") {
                    let pdfurl;
                    let pdfName;
                    if (img?.includes(".pdf")) {
                      pdfName = new URL(img).searchParams.get("filename");
                      pdfurl = img.split("?filename=")[0];
                    }
                    console.log(img);
                    return (
                      <>
                        {img?.includes(".pdf") ? (
                          <p className="form-group d-flex flex-row">
                            <a
                              href={pdfurl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {pdfName}
                            </a>

                            <i
                              className="fa-solid fa-xmark"
                              onClick={() => removeImage(index)}
                              style={{
                                backgroundColor: "transparent",
                                color: "black",
                                border: "none",
                                borderRadius: "50%",
                                width: "24px",
                                height: "24px",
                                cursor: "pointer",
                              }}
                            ></i>
                          </p>
                        ) : (
                          <div
                            key={index}
                            style={{
                              position: "relative",
                              display: "inline-block",
                              width: "200px",
                              border: "2px solid #000",
                            }}
                          >
                            <img
                              src={
                                img?.includes("data:image")
                                  ? img
                                  : ImageProxyBaseUrl + encodeURIComponent(img)
                              }
                              alt={`Captured ${index + 1}`}
                              style={{
                                width: "100%",
                                height: "auto",
                                display: "block",
                              }}
                            />

                            <i
                              className="fa-solid fa-xmark"
                              onClick={() => removeImage(index)}
                              style={{
                                position: "absolute",
                                top: "5px",
                                right: "5px",
                                backgroundColor: "transparent",
                                color: "black",
                                border: "none",
                                borderRadius: "50%",
                                width: "24px",
                                height: "24px",
                                cursor: "pointer",
                              }}
                            ></i>
                          </div>
                        )}
                      </>
                    );
                  }
                  <div
                    key={index}
                    style={{
                      position: "relative",
                      display: "inline-block",
                      width: "200px",
                      border: "2px solid #000",
                    }}
                  >
                    <img
                      src={
                        img?.includes("data:image")
                          ? img
                          : ImageProxyBaseUrl + encodeURIComponent(img)
                      }
                      alt={`Captured ${index + 1}`}
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                      }}
                    />

                    <i
                      className="fa-solid fa-xmark"
                      onClick={() => removeImage(index)}
                      style={{
                        position: "absolute",
                        top: "5px",
                        right: "5px",
                        backgroundColor: "transparent",
                        color: "black",
                        border: "none",
                        borderRadius: "50%",
                        width: "24px",
                        height: "24px",
                        cursor: "pointer",
                      }}
                    ></i>
                  </div>;
                })}
              </div>
            </div>
          )}
          <div className="row mt-5">
            <div>
              <div className="card-body action_btns d-flex justify-content-end gap-3 flex-wrap flex-sm-nowrap">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => handleCancel()}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={savedImages?.length === 0 || isLoading}
                  onClick={() => savePropertyImage()}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UploadPropertyImage;
